import { makeAutoObservable } from "mobx";
import { getEnv } from "mobx-easy";
import { DBAction } from "../../../enum/DBAction";
import { StoreState } from "../../../enum/StoreState";
import { RootEnv } from "../../setup/create-store";
import { IOperationRuleLocationItem } from "../operationRuleLocationInsights/operationRuleLocationInsightModel";
import {
  IOperationRuleLocation,
  ILocationMenuItem,
  ILocationMenuInsight,
} from "../operationRuleLocations/OperationRuleLocationModel";
import { IOperationRule } from "../operationRules/OperationRuleModel";
import {
  AlertConfig,
  AlertModule,
  IOperationRuleAlert,
  IOperationRuleAlertForTable,
  IOperationRuleAlertUser,
  ISensorAlertConfigLightning,
  OperationRuleAlertForTableAPIParams,
} from "./insightAlertModel";

import i18n from "../../../i18n";
import { LatLngLiteral } from "leaflet";
import {
  IEnvironmentalVariable,
  IStation,
} from "../oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { MacroRegion } from "enum/MacroRegion";
import { CreateAlertFormValues } from "pages/Alert/types";
import { sortLocationMenuList } from "./sortLocationMenuList";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import { ConditionGroup } from "./insightAlertModel";
import { EnvironmentalType } from "enum/EnvironmentalType";

export default class InsightAlertStore {
  insightAlertModalVisible: boolean = false;

  operationRule: IOperationRule[] = [];

  operationRuleDetailSelected: number = 0;

  operationRuleLocation: IOperationRuleLocation[] = [];

  operationRuleLocationDetailSelected: number = 0;

  alertTypeDetailSelected: string | null = null;

  operationRuleLocationInsight: IOperationRuleLocationItem[] = [];

  operationRuleModal: IOperationRule[] = [];

  operationRuleLocationModal: IOperationRuleLocation[] = [];

  operationRuleLocationInsightModal: IOperationRuleLocationItem[] = [];

  operationRuleAlertUser: IOperationRuleAlertUser[] = [];

  operationRuleAlertUserSelected: IOperationRuleAlertUser[] = [];

  operationRuleAlertUserSelectedBeforeEdit: IOperationRuleAlertUser[] = [];

  operationRuleAlert: IOperationRuleAlert | null = null;

  state: string = StoreState.DONE;

  locationState: string = StoreState.DONE;

  messageError: string = "";

  operationRuleAlertForTableDatail: IOperationRuleAlertForTable[] = [];

  operationRuleAlertForTableForEdit: IOperationRuleAlertForTable;

  operationRuleAlertAction: DBAction = DBAction.CREATE;

  alertTemplates: AlertConfig[] = [];

  // New: Separate arrays for custom and default templates from alert-template-config endpoint
  customAlertTemplates: AlertConfig[] = [];

  defaultAlertTemplates: AlertConfig[] = [];

  alertTypes: string[] = ["port_closure", "extreme_wind", "extreme_rain"];

  selectedAlertType: string | null = null;

  selectedAlertModule: AlertModule = AlertModule.INSIGHT;

  isFilterApplied: boolean = false;

  selectedGeofence: LatLngLiteral[] = [];

  environmentalVariables: IEnvironmentalVariable[] = [];

  isLoadingEnvironmentalVariables: boolean = false;

  hasErrorLoadingEnvironmentalVariables: boolean = false;

  lastEnvironmentalVariablesParams: {
    macroRegionOrigin: string;
    stationId?: number;
    latitude?: number;
    longitude?: number;
  } | null = null;

  macroRegionOrigin: MacroRegion | null = null;

  isGeofenceInvalid: boolean = false;

  isPointInvalid: boolean = false;

  createFormValues: CreateAlertFormValues | null = null;

  isGeofenceLoadedByGeoJson: boolean = false;

  areaSelectorVisible: boolean = false;

  submissionState: string = StoreState.DONE;

  alertDetailState: string = StoreState.DONE;

  isGeofenceSavedToLocation: boolean = false;

  alertLocationType: "point" | "area" | null = null;

  alertCategory: "standalone" | "insight" = "standalone";

  selectedStationForAlert: IStation | null = null;

  // Stores the original station before entering edit mode (for cancel/restore)
  originalStationForAlert: IStation | null = null;

  // Location menu list for insight alert selection
  locationMenuList: ILocationMenuItem[] = [];
  locationMenuListState: string = StoreState.DONE;
  selectedInsight: ILocationMenuInsight | null = null;
  selectedInsightLocationId: number | null = null;
  selectedDashboardIdForEdit: number | null = null;

  // Stores the original template conditions for reset comparison (persists across drawer remount)
  originalTemplateConditions: ConditionGroup[] | null = null;

  // Sensor alert observables
  sensorMeasuredData: ISensorMeasuredData[] = [];
  selectedSensorIds: number[] = [];
  sensorConditions: ConditionGroup[] = [];
  sensorDataState: StoreState = StoreState.DONE;

  /**
   * Spec 043 — canonical lightning slice maintained while the drawer is closed
   * (e.g. while the user is on the /forecast-charts sensor-selection gateway).
   * The drawer hydrates from this on remount when returning from the gateway,
   * and the gateway mutates it directly via `setLightningSelection`.
   */
  lightningSelection: ISensorAlertConfigLightning | null = null;

  sensorEnvironmentalVariables: IEnvironmentalVariable[] = [];
  isLoadingSensorEnvironmentalVariables: boolean = false;
  hasErrorLoadingSensorEnvironmentalVariables: boolean = false;
  sensorEnvVarsRequestId: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setAlertLocationType(value: "point" | "area" | null) {
    this.alertLocationType = value;
  }

  setAlertCategory(value: "standalone" | "insight") {
    this.alertCategory = value;
  }

  setLocationMenuList(value: ILocationMenuItem[]) {
    this.locationMenuList = value;
  }

  setOriginalTemplateConditions(value: ConditionGroup[] | null) {
    this.originalTemplateConditions = value;
  }

  setLocationMenuListState(value: StoreState) {
    this.locationMenuListState = value;
  }

  setSelectedInsight(value: ILocationMenuInsight | null) {
    this.selectedInsight = value;
  }

  setSelectedInsightLocationId(value: number | null) {
    this.selectedInsightLocationId = value;
  }

  setSelectedDashboardIdForEdit(value: number | null) {
    this.selectedDashboardIdForEdit = value;
  }

  setSelectedStationForAlert(
    value: IStation | null,
    macroRegionOverride?: MacroRegion | string | null,
  ) {
    const previousStationId = this.selectedStationForAlert?.station_id;
    this.selectedStationForAlert = value;

    // Update macroRegionOrigin BEFORE reload if caller provides it
    // This prevents the reload from using a stale macro region
    if (macroRegionOverride !== undefined) {
      this.macroRegionOrigin = macroRegionOverride as MacroRegion | null;
    }

    // Reload environmental variables when station changes
    if (this.macroRegionOrigin && value !== null) {
      const currentStationId = value.station_id;
      // Only reload if station actually changed
      if (previousStationId !== currentStationId) {
        this.reloadEnvironmentalVariablesForStation(value);
      }
    }
  }

  /**
   * Reload environmental variables based on station selection
   * Called automatically when selectedStationForAlert changes
   */
  private reloadEnvironmentalVariablesForStation(station: IStation) {
    if (!this.macroRegionOrigin) return;

    const params: {
      macroRegionOrigin: string;
      stationId?: number;
      latitude?: number;
      longitude?: number;
      forceReload?: boolean;
    } = {
      macroRegionOrigin: this.macroRegionOrigin,
      forceReload: true,
    };

    if (station.station_id > 0) {
      // Existing station
      params.stationId = station.station_id;
    } else if (station.station_id === 0) {
      // Custom point (no station)
      params.latitude = station.station_lat;
      params.longitude = station.station_lon;
    }

    this.getEnvironmentalVariables(params);
  }

  setOriginalStationForAlert(value: IStation | null) {
    this.originalStationForAlert = value;
  }

  setSelectedAlertType(value: string | null) {
    this.selectedAlertType = value;
  }

  setInsightAlertModalVisible(value: boolean) {
    this.insightAlertModalVisible = value;
  }

  setOperationRule(value: IOperationRule[]) {
    this.operationRule = value;
  }

  setOperationRuleLocation(value: IOperationRuleLocation[]) {
    this.operationRuleLocation = value;
  }

  setOperationRuleLocationInsight(value: IOperationRuleLocationItem[]) {
    this.operationRuleLocationInsight = value;
  }

  setOperationRuleModal(value: IOperationRule[]) {
    this.operationRuleModal = value;
  }

  setOperationRuleLocationModal(value: IOperationRuleLocation[]) {
    this.operationRuleLocationModal = value;
  }

  setOperationRuleLocationInsightModal(value: IOperationRuleLocationItem[]) {
    this.operationRuleLocationInsightModal = value;
  }

  setOperationRuleAlertUser(value: IOperationRuleAlertUser[]) {
    this.operationRuleAlertUser = value;
  }

  setOperationRuleAlertUserSelected(value: IOperationRuleAlertUser[]) {
    this.operationRuleAlertUserSelected = value;
  }

  setOperationRuleAlertUserSelectedBeforeEdit(
    value: IOperationRuleAlertUser[],
  ) {
    this.operationRuleAlertUserSelectedBeforeEdit = value;
  }

  setOperationRuleLocationDetailSelected(value: number) {
    this.operationRuleLocationDetailSelected = value;
  }

  setAlertTypeDetailSelected(value: string | null) {
    this.alertTypeDetailSelected = value;
  }

  setOperationRuleDetailSelected(value: number) {
    this.operationRuleDetailSelected = value;
  }

  setState(value: StoreState) {
    this.state = value;
  }

  setLocationState(value: StoreState) {
    this.locationState = value;
  }

  setMessageError(value: string) {
    this.messageError = value;
  }

  setOperationRuleAlertForTableDatail(value: IOperationRuleAlertForTable[]) {
    this.operationRuleAlertForTableDatail = value;
  }

  setOperationRuleAlertForTableForEdit(value: IOperationRuleAlertForTable) {
    this.operationRuleAlertForTableForEdit = value;
  }

  setOperationRuleAlert(value: IOperationRuleAlert | null) {
    this.operationRuleAlert = value;
  }

  setOperationRuleAlertAction(value: DBAction) {
    this.operationRuleAlertAction = value;
  }

  setAlertTemplates(value: AlertConfig[]) {
    this.alertTemplates = value;
  }

  setCustomAlertTemplates(value: AlertConfig[]) {
    this.customAlertTemplates = value;
  }

  setDefaultAlertTemplates(value: AlertConfig[]) {
    this.defaultAlertTemplates = value;
  }

  setSelectedAlertModule(value: AlertModule) {
    this.selectedAlertModule = value;
  }

  setIsFilterApplied(value: boolean) {
    this.isFilterApplied = value;
  }

  setOperationRuleAlertForTableActive(id: number, active: boolean) {
    const index = this.operationRuleAlertForTableDatail.findIndex(
      (item) => item.id === id,
    );
    if (index !== -1) {
      this.operationRuleAlertForTableDatail[index].active = active;
    }
  }

  setSelectedGeofence(value: LatLngLiteral[][] | LatLngLiteral[]) {
    const previousLength = this.selectedGeofence?.length ?? 0;

    if (value?.length > 0 && Array.isArray(value[0])) {
      this.selectedGeofence = (value as LatLngLiteral[][]).flat();
    } else {
      this.selectedGeofence = value as LatLngLiteral[];
    }

    // Reload environmental variables when geofence changes (area selection)
    const newLength = this.selectedGeofence?.length ?? 0;
    if (
      this.macroRegionOrigin &&
      newLength > 0 &&
      previousLength !== newLength
    ) {
      this.reloadEnvironmentalVariablesForArea();
    }
  }

  /**
   * Reload environmental variables for area selection
   * Called automatically when selectedGeofence changes
   */
  private reloadEnvironmentalVariablesForArea() {
    if (!this.macroRegionOrigin) return;

    // For area selection, we only pass macroRegionOrigin
    this.getEnvironmentalVariables({
      macroRegionOrigin: this.macroRegionOrigin,
      forceReload: true,
    });
  }

  setEnvironmentalVariables(value: IEnvironmentalVariable[]) {
    this.environmentalVariables = value;
  }

  setMacroRegionOrigin(value: MacroRegion | null) {
    this.macroRegionOrigin = value;
  }

  setIsGeofenceInvalid(value: boolean) {
    this.isGeofenceInvalid = value;
  }

  setIsPointInvalid(value: boolean) {
    this.isPointInvalid = value;
  }

  setCreateFormValues(values: CreateAlertFormValues | null) {
    this.createFormValues = values;
  }

  setIsGeofenceLoadedByGeoJson(value: boolean) {
    this.isGeofenceLoadedByGeoJson = value;
  }

  setAreaSelectorVisible(value: boolean) {
    this.areaSelectorVisible = value;
  }

  setSubmissionState(value: StoreState) {
    this.submissionState = value;
  }

  setAlertDetailState(value: StoreState) {
    this.alertDetailState = value;
  }

  setIsGeofenceSavedToLocation(value: boolean) {
    this.isGeofenceSavedToLocation = value;
  }

  setSensorMeasuredData(value: ISensorMeasuredData[]) {
    this.sensorMeasuredData = value;
  }

  setSelectedSensorIds(value: number[]) {
    this.selectedSensorIds = value;
  }

  /** Spec 043 — set/replace the canonical lightning slice (drawer + gateway). */
  setLightningSelection(value: ISensorAlertConfigLightning | null) {
    this.lightningSelection = value;
  }

  /** Spec 043 — clear the canonical lightning slice. */
  clearLightningSelection() {
    this.lightningSelection = null;
  }

  setSensorConditions(value: ConditionGroup[]) {
    this.sensorConditions = value;
  }

  setSensorDataState(value: StoreState) {
    this.sensorDataState = value;
  }

  setSensorEnvironmentalVariables(value: IEnvironmentalVariable[]) {
    this.sensorEnvironmentalVariables = value;
  }

  async loadSensorEnvironmentalVariables(macroRegionOrigin: string) {
    this.sensorEnvVarsRequestId = this.sensorEnvVarsRequestId + 1;
    const currentRequestId = this.sensorEnvVarsRequestId;

    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.isLoadingSensorEnvironmentalVariables = true;
      this.hasErrorLoadingSensorEnvironmentalVariables = false;
      const variables: IEnvironmentalVariable[] =
        await insightAlertService.getSensorEnvironmentalVariables({
          macroRegionOrigin,
        });
      if (currentRequestId !== this.sensorEnvVarsRequestId) return;
      this.setSensorEnvironmentalVariables(variables);
    } catch (error: any) {
      if (currentRequestId !== this.sensorEnvVarsRequestId) return;
      this.hasErrorLoadingSensorEnvironmentalVariables = true;
      this.setSensorEnvironmentalVariables([]);
      this.setMessageError(
        i18n.t(
          "oceanWeatherIntelligence.insightAlert.errorLoadingEnvironmentalVariables",
        ),
      );
    } finally {
      if (currentRequestId === this.sensorEnvVarsRequestId) {
        this.isLoadingSensorEnvironmentalVariables = false;
      }
    }
  }

  async loadSensors() {
    // Skip if already loading to avoid redundant API calls
    if (this.sensorDataState === StoreState.PEDDING) return;

    const { sensorService } = getEnv<RootEnv>();
    this.setSensorDataState(StoreState.PEDDING);
    try {
      const data = await sensorService.getSensorMakerLocations({
        envType: EnvironmentalType.PRECIPITATION,
      });
      this.setSensorMeasuredData(data);
      this.setSensorDataState(StoreState.DONE);
    } catch (error: any) {
      this.setSensorMeasuredData([]);
      this.setSensorDataState(StoreState.ERROR);
    }
  }

  clearSensorSelection() {
    this.setSelectedSensorIds([]);
    this.setSensorConditions([]);
    // Do NOT clear sensorMeasuredData/sensorDataState here.
    // The sensor data is location-dependent and managed by loadSensors().
    // Clearing it causes a stale empty state when the drawer reopens
    // without a location change (the effect won't re-trigger).
  }

  reset() {
    this.setState(StoreState.DONE);
    this.setLocationState(StoreState.DONE);
    this.setSubmissionState(StoreState.DONE);
    this.setAlertDetailState(StoreState.DONE);

    this.setOperationRuleAlertUser([]);
    this.setOperationRuleAlertForTableDatail([]);
    this.setAlertTemplates([]);
    this.setCustomAlertTemplates([]);
    this.setDefaultAlertTemplates([]);
    this.setSelectedAlertModule(AlertModule.INSIGHT);
    this.setIsFilterApplied(false);
    this.setSelectedGeofence([]);
    this.setEnvironmentalVariables([]);
    this.isLoadingEnvironmentalVariables = false;
    this.hasErrorLoadingEnvironmentalVariables = false;
    this.lastEnvironmentalVariablesParams = null;
    this.setMacroRegionOrigin(null);
    this.setIsGeofenceInvalid(false);
    this.setIsPointInvalid(false);
    this.setCreateFormValues(null);
    this.setIsGeofenceLoadedByGeoJson(false);
    this.setAreaSelectorVisible(false);
    this.setIsGeofenceSavedToLocation(false);
    this.setSelectedStationForAlert(null);
    this.setAlertLocationType(null);
    this.setAlertCategory("standalone");
    this.clearSensorSelection();
    // Full reset also clears cached sensor data
    this.setSensorMeasuredData([]);
    this.setSensorDataState(StoreState.DONE);
    this.setSensorEnvironmentalVariables([]);
    this.isLoadingSensorEnvironmentalVariables = false;
    this.hasErrorLoadingSensorEnvironmentalVariables = false;
  }

  async setActive(operationRuleAlertId: number, isActive: boolean) {
    const { insightAlertService } = getEnv<RootEnv>();
    try {
      await insightAlertService.setActive(operationRuleAlertId, isActive);
      this.setOperationRuleAlertForTableActive(operationRuleAlertId, isActive);
    } catch (error: any) {
      this.setState(StoreState.ERROR);
      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorUpdateAlert"),
        );
      }
    }
  }

  async getOperationRuleAlertUserAPI({
    operationRuleAlertId,
    lang,
  }: {
    operationRuleAlertId?: number;
    lang?: string;
  }) {
    const { operationRuleAlertUserService } = getEnv<RootEnv>();
    try {
      this.setOperationRuleAlertUser([]);
      this.setAlertDetailState(StoreState.PEDDING);
      const operationRuleAlertUsers: IOperationRuleAlertUser[] =
        await operationRuleAlertUserService.getOperationRuleAlertUser(
          operationRuleAlertId,
        );
      if (operationRuleAlertUsers?.length) {
        operationRuleAlertUsers.sort((usera, userb) => {
          const whatsappDiff =
            (userb.isWhatsappAllowed ? 1 : 0) -
            (usera.isWhatsappAllowed ? 1 : 0);
          if (whatsappDiff !== 0) return whatsappDiff;

          const hasIdA = usera.id != null ? 1 : 0;
          const hasIdB = userb.id != null ? 1 : 0;
          if (hasIdA !== hasIdB) return hasIdB - hasIdA;

          return usera.userName.localeCompare(userb.userName, lang, {
            sensitivity: "base",
          });
        });

        this.setOperationRuleAlertUser(operationRuleAlertUsers);
      }

      this.setAlertDetailState(StoreState.DONE);
    } catch (error) {
      this.setAlertDetailState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetUserAlert"),
      );
    }
  }

  async getOperationRuleAPI() {
    const { operationRuleService } = getEnv<RootEnv>();

    try {
      this.setOperationRule([]);
      const operationRule: IOperationRule[] =
        await operationRuleService.getOperationRule();
      if (operationRule.length > 0) {
        this.setOperationRule(operationRule);
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetDashboardAlert"),
      );
    }
  }

  async getOperationRuleModalAPI() {
    const { operationRuleService } = getEnv<RootEnv>();

    try {
      this.setOperationRuleModal([]);
      const operationRuleModal: IOperationRule[] =
        await operationRuleService.getOperationRule();
      if (operationRuleModal.length > 0) {
        this.setOperationRuleModal(operationRuleModal);
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetDashboardAlert"),
      );
    }
  }

  async getOperationRuleLocationAPI(operationRuleId: number) {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    try {
      this.setOperationRuleLocation([]);
      this.setLocationState(StoreState.PEDDING);
      const operationRuleLocation: IOperationRuleLocation[] =
        await operationRuleLocationService.getOperationRuleLocation({
          operationRuleId,
        });
      if (operationRuleLocation.length > 0) {
        this.setOperationRuleLocation(operationRuleLocation);
      }

      this.setLocationState(StoreState.DONE);
    } catch (error) {
      this.setLocationState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetLocationAlert"),
      );
    }
  }

  async getOperationRuleLocationModalAPI(operationRuleId: number) {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    try {
      this.setOperationRuleLocationModal([]);
      this.setState(StoreState.PEDDING);
      const operationRuleLocationModal: IOperationRuleLocation[] =
        await operationRuleLocationService.getOperationRuleLocation({
          operationRuleId,
        });
      if (operationRuleLocationModal.length > 0) {
        this.setOperationRuleLocationModal(operationRuleLocationModal);
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetLocationAlert"),
      );
    }
  }

  async getOperationRuleLocationInsightAPI(operationRuleLocationId: number) {
    const { operationRuleLocationInsightService } = getEnv<RootEnv>();

    try {
      this.setOperationRuleLocationInsight([]);
      this.setState(StoreState.PEDDING);
      const operationRuleLocationInsight: IOperationRuleLocationItem[] =
        await operationRuleLocationInsightService.getOperationRuleLocationInsight(
          operationRuleLocationId,
        );
      if (operationRuleLocationInsight.length > 0) {
        this.setOperationRuleLocationInsight(operationRuleLocationInsight);
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetInsightAlert"),
      );
    }
  }

  async getOperationRuleLocationInsightModalAPI(
    operationRuleLocationId: number,
  ) {
    const { operationRuleLocationInsightService } = getEnv<RootEnv>();

    try {
      this.setOperationRuleLocationInsightModal([]);
      this.setState(StoreState.PEDDING);
      const operationRuleLocationInsightModal: IOperationRuleLocationItem[] =
        await operationRuleLocationInsightService.getOperationRuleLocationInsight(
          operationRuleLocationId,
        );
      if (operationRuleLocationInsightModal.length > 0) {
        this.setOperationRuleLocationInsightModal(
          operationRuleLocationInsightModal,
        );
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetInsightAlert"),
      );
    }
  }

  async getLocationMenuListAPI(dashboardId: number) {
    const { operationRuleLocationService } = getEnv<RootEnv>();
    try {
      this.setLocationMenuList([]);
      this.setLocationMenuListState(StoreState.PEDDING);
      const menuList = await operationRuleLocationService.getLocationMenuList(
        dashboardId,
      );

      this.setLocationMenuList(sortLocationMenuList(menuList));
      this.setLocationMenuListState(StoreState.DONE);
    } catch (error) {
      this.setLocationMenuListState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetInsightAlert"),
      );
    }
  }

  async getOperationRuleAlertForTableAPI({
    operationRuleId,
    operationRuleLocationId,
    alertType,
    operationRuleLocationItemIds,
  }: OperationRuleAlertForTableAPIParams) {
    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.setOperationRuleAlertForTableDatail([]);
      this.setState(StoreState.PEDDING);
      const insightAlert: IOperationRuleAlertForTable[] =
        await insightAlertService.getOperationRuleAlertForTable(
          operationRuleId,
          operationRuleLocationId,
          alertType,
          operationRuleLocationItemIds,
        );
      if (insightAlert.length > 0) {
        // order by alertType
        insightAlert.sort((a, b) => {
          const aId = a?.alertType ?? "";
          const bId = b?.alertType ?? "";
          return aId < bId ? 1 : aId > bId ? -1 : 0;
        });
        this.setOperationRuleAlertForTableDatail(insightAlert);
      }
      if (this.state === StoreState.PEDDING) {
        this.setState(StoreState.DONE);
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
      );
    }
  }

  async getOperationRuleAlertAPI(operationRuleAlertId: number) {
    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.setAlertDetailState(StoreState.PEDDING);
      const insightAlert: IOperationRuleAlert =
        await insightAlertService.getOperationRuleAlert(operationRuleAlertId);
      if (insightAlert) {
        this.setOperationRuleAlert(insightAlert);
      }
      this.setAlertDetailState(StoreState.DONE);
    } catch (error) {
      this.setAlertDetailState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
      );
    }
  }

  /**
   * Spec 043 — load every OperationRuleAlert row in a group (rain + lightning
   * for the same `operationRuleLocationItemId`) and merge them into a single
   * virtual `operationRuleAlert` so the drawer can edit both slices as one
   * logical alert.
   *
   * Falls back to a single-row load when the group has just one id (legacy
   * standalone alerts, or events with only one configured slice).
   */
  async getOperationRuleAlertGroupAPI(groupIds: number[]) {
    if (groupIds.length === 0) return;
    if (groupIds.length === 1) {
      return this.getOperationRuleAlertAPI(groupIds[0]);
    }

    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.setAlertDetailState(StoreState.PEDDING);
      // Use allSettled so a single stale id (e.g. listing referenced a row
      // that was deleted by a sibling reconcile and the listing didn't
      // refetch yet) doesn't poison the entire group load.
      const settled = await Promise.allSettled(
        groupIds.map((id) => insightAlertService.getOperationRuleAlert(id)),
      );
      const valid = settled
        .filter(
          (s): s is PromiseFulfilledResult<IOperationRuleAlert> =>
            s.status === "fulfilled" && !!s.value,
        )
        .map((s) => s.value);
      if (valid.length === 0) {
        this.setAlertDetailState(StoreState.ERROR);
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
        );
        return;
      }

      // Rain row holds the shared metadata + sensors/conditions; lightning
      // row holds the lightning slice. Prefer rain as the "primary" since
      // most metadata fields (recipients, channels, etc.) match across rows
      // but the rain row carries the canonical sensor list.
      const rainRow = valid.find(
        (r) => (r.sensorAlertConfig?.sensors?.length ?? 0) > 0,
      );
      const lightningRow = valid.find((r) => !!r.sensorAlertConfig?.lightning);
      const primary = rainRow ?? lightningRow ?? valid[0];

      const merged: IOperationRuleAlert = {
        ...primary,
        sensorAlertConfig: {
          sensors: primary.sensorAlertConfig?.sensors ?? [],
          conditions: primary.sensorAlertConfig?.conditions ?? [],
          alertFrequency: primary.sensorAlertConfig?.alertFrequency,
          lightning: lightningRow?.sensorAlertConfig?.lightning,
        },
      };
      this.setOperationRuleAlert(merged);
      this.setAlertDetailState(StoreState.DONE);
    } catch (error) {
      this.setAlertDetailState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
      );
    }
  }

  /**
   * Spec 043 — delete every OperationRuleAlert row tied to an event group
   * by calling `PUT /event/:id` with `alert: null`. The backend cascades
   * the deletion across all rows sharing the `operationRuleLocationItemId`
   * (rain + lightning) within a single transaction.
   *
   * Use this for trash on grouped table rows. Standalone alerts (no event
   * link) still go through `deleteOperationRuleAlertAPI`.
   */
  async deleteAlertGroupViaEventAPI(operationRuleLocationItemId: number) {
    const { eventService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await eventService.updateEvent(operationRuleLocationItemId, {
        alert: null,
      });
      this.setOperationRuleAlertForTableDatail(
        this.operationRuleAlertForTableDatail.filter(
          (item) => item.operationRuleLocationItemId !== operationRuleLocationItemId,
        ),
      );
      this.setState(StoreState.DONE);
    } catch (error: any) {
      this.setState(StoreState.ERROR);
      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorDeleteAlerts"),
        );
      }
    }
  }

  async deleteOperationRuleAlertAPI(operationRuleAlertId: number) {
    const { insightAlertService } = getEnv<RootEnv>();

    try {
      this.setState(StoreState.PEDDING);
      let resultState = StoreState.DONE;
      const deleteAlertResult: boolean =
        await insightAlertService.deleteOperationRuleAlert(
          operationRuleAlertId,
        );
      if (!deleteAlertResult) {
        resultState = StoreState.ERROR;
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorDeleteAlerts"),
        );
      }
      this.setState(resultState);
      if (resultState === StoreState.DONE) {
        this.setOperationRuleAlertForTableDatail(
          this.operationRuleAlertForTableDatail.filter(
            (item) => item.id !== operationRuleAlertId,
          ),
        );
      }
    } catch (error: any) {
      this.setState(StoreState.ERROR);
      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorDeleteAlerts"),
        );
      }
    }
  }

  async addOperationRuleAlertAPI(operationRuleAlert: IOperationRuleAlert) {
    const { insightAlertService } = getEnv<RootEnv>();

    try {
      this.setState(StoreState.PEDDING);
      this.setSubmissionState(StoreState.PEDDING);

      const operationRuleAlertInserted: IOperationRuleAlert =
        await insightAlertService.addOperationRuleAlert(operationRuleAlert);
      if (operationRuleAlertInserted) {
        await this.getOperationRuleAlertForTableAPI({});

        this.setState(StoreState.DONE);
        this.setSubmissionState(StoreState.DONE);
      } else {
        this.setState(StoreState.ERROR);
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorAddAlert"),
        );
      }
    } catch (error: any) {
      this.setState(StoreState.ERROR);
      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else if (error.response?.status === 409) {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorConflict"),
        );
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorAddAlert"),
        );
      }
    }
  }

  async updateOperationRuleAlertAPI() {
    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      this.setSubmissionState(StoreState.PEDDING);

      if (!this.operationRuleAlert) return;

      const originalTableData = this.operationRuleAlertForTableForEdit;

      const { conditions, station, ...alertWithoutLocalFields } =
        this.operationRuleAlert;
      const payloadForBackend = this.operationRuleAlert
        .operationRuleLocationItemId
        ? alertWithoutLocalFields
        : this.operationRuleAlert;

      const operationRuleAlertUpdated =
        await insightAlertService.updateOperationRuleAlert(payloadForBackend);
      if (operationRuleAlertUpdated) {
        const alertForTable = this.buildAlertForTable({
          ...operationRuleAlertUpdated,
          area: this.operationRuleAlert.area,
        });

        const originalInsightId =
          this.operationRuleAlertForTableForEdit?.operationRuleLocationItemId;
        const insightWasChanged =
          this.selectedInsight !== null &&
          this.selectedInsight.id !== originalInsightId;

        let newDashboardId: number | undefined;
        let newDashboardName: string | undefined;
        let newInsightName: string | undefined;
        let newLocationId: number | undefined;
        let newLocationName: string | undefined;

        if (
          insightWasChanged &&
          this.selectedDashboardIdForEdit &&
          this.selectedInsight &&
          this.operationRule.length > 0
        ) {
          newDashboardId = this.selectedDashboardIdForEdit;
          newDashboardName = this.operationRule.find(
            (d) => d.id === newDashboardId,
          )?.name;
          newInsightName = this.selectedInsight.name;

          const locationWithInsight = this.locationMenuList.find((loc) =>
            loc.items?.some((item) => item.id === this.selectedInsight?.id),
          );
          if (locationWithInsight) {
            newLocationId = locationWithInsight.id;
            newLocationName = locationWithInsight.name;
          }
        }

        const preservedAlertForTable: IOperationRuleAlertForTable = {
          ...alertForTable,
          operationRuleId: insightWasChanged
            ? newDashboardId
            : alertForTable.operationRuleId ??
              originalTableData?.operationRuleId,
          operationRuleName: insightWasChanged
            ? newDashboardName
            : alertForTable.operationRuleName ??
              originalTableData?.operationRuleName,
          operationRuleLocationId: insightWasChanged
            ? newLocationId
            : alertForTable.operationRuleLocationId ??
              originalTableData?.operationRuleLocationId,
          operationRuleLocationName: insightWasChanged
            ? newLocationName
            : alertForTable.operationRuleLocationName ??
              originalTableData?.operationRuleLocationName,
          operationRuleLocationItemId:
            operationRuleAlertUpdated.operationRuleLocationItemId,
          operationRuleLocationItemName: insightWasChanged
            ? newInsightName
            : alertForTable.operationRuleLocationItemName ??
              originalTableData?.operationRuleLocationItemName,
        };

        const insightAlert = this.operationRuleAlertForTableDatail.map((item) =>
          item.id === operationRuleAlertUpdated.id
            ? preservedAlertForTable
            : item,
        );

        this.setOperationRuleAlertForTableDatail(insightAlert);

        this.setOperationRuleAlertForTableForEdit(preservedAlertForTable);

        this.setState(StoreState.DONE);
        this.setSubmissionState(StoreState.DONE);
      } else {
        this.setState(StoreState.ERROR);
        this.setSubmissionState(StoreState.ERROR);
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorUpdateAlert"),
        );
      }
    } catch (error: any) {
      this.setState(StoreState.ERROR);
      this.setSubmissionState(StoreState.ERROR);
      if (error.response?.status === 403) {
        this.setMessageError(i18n.t("youCanOnlyChangeItems"));
      } else if (error.response?.status === 409) {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorConflict"),
        );
      } else {
        this.setMessageError(
          i18n.t("oceanWeatherIntelligence.insightAlert.errorUpdateAlert"),
        );
      }
    }
  }

  buildAlertForTable(
    operationRuleAlert: IOperationRuleAlert,
  ): IOperationRuleAlertForTable {
    const operationRuleLocationItemId: number | undefined =
      operationRuleAlert?.operationRuleLocationItemId;
    let operationRuleLocationItemName: string | undefined;
    let operationRuleLocationId: number | undefined;
    let operationRuleLocationName: string | undefined;
    let operationRuleId: number | undefined;
    let operationRuleName: string | undefined;

    if (operationRuleLocationItemId) {
      /* insight*/

      operationRuleLocationItemName =
        this.operationRuleLocationInsightModal.find(
          (item) => item.id === operationRuleLocationItemId,
        )?.name;

      /*location*/
      operationRuleLocationId = this.operationRuleLocationInsightModal.find(
        (item) => item.id === operationRuleAlert.operationRuleLocationItemId,
      )?.operationRuleLocationId;
      const localtion = this.operationRuleLocationModal.find(
        (item) => item.id === operationRuleLocationId,
      );

      /*location*/
      operationRuleLocationName = localtion?.name;

      /*Dashboard*/
      operationRuleId = localtion?.operationRuleId;
      operationRuleName = this.operationRule.find(
        (item) => item.id === operationRuleId,
      )?.name;
    }

    return {
      id: operationRuleAlert.id,
      name: operationRuleAlert.name,
      operationRuleLocationItemId,
      operationRuleLocationItemName,
      operationRuleLocationId,
      operationRuleLocationName,
      operationRuleId,
      operationRuleName,
      alertType: operationRuleAlert.alertType,
      active: operationRuleAlert.active,
      area: operationRuleAlert.area || "",
    };
  }

  getAlertTemplateByConfigId(configId: string): AlertConfig | undefined {
    return this.alertTemplates.find(
      (template) => template.configId === configId,
    );
  }

  /**
   * Get template for a specific risk type and macro region
   * First tries to find a custom template, then falls back to default
   * @param alertType The backend alert type (e.g., "extreme_rain", "extreme_wind")
   * @param macroRegion The macro region origin (e.g., "eps", "gfs")
   * @returns The matching template or undefined if none found
   */
  getTemplateForRisk(
    alertType: string,
    macroRegion: string,
  ): AlertConfig | undefined {
    const macroRegionLower = macroRegion.toLowerCase();

    // First, try to find a custom template for this alertType and macroRegion
    const customTemplate = this.customAlertTemplates.find(
      (template) =>
        template.alertType === alertType &&
        template.macroregiao?.toLowerCase() === macroRegionLower,
    );

    if (customTemplate) {
      return customTemplate;
    }

    // Fall back to default template (which doesn't have macroregiao)
    const defaultTemplate = this.defaultAlertTemplates.find(
      (template) => template.alertType === alertType,
    );

    return defaultTemplate;
  }

  async getAlertTemplateConfig() {
    const { insightAlertService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const response = await insightAlertService.getAlertTemplateConfig();

      // Store both custom and default templates separately
      this.setCustomAlertTemplates(response.custom || []);
      this.setDefaultAlertTemplates(response.default || []);

      // Also maintain backwards compatibility with alertTemplates
      // by combining custom (priority) + default templates
      const allTemplates = [
        ...(response.custom || []),
        ...(response.default || []),
      ];
      this.setAlertTemplates(allTemplates);

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
      );
    }
  }

  private hasMatchingParams(nextParams: {
    macroRegionOrigin: string;
    stationId?: number;
    latitude?: number;
    longitude?: number;
  }): boolean {
    if (!this.lastEnvironmentalVariablesParams) return false;

    return (
      this.lastEnvironmentalVariablesParams.macroRegionOrigin ===
        nextParams.macroRegionOrigin &&
      this.lastEnvironmentalVariablesParams.stationId ===
        nextParams.stationId &&
      this.lastEnvironmentalVariablesParams.latitude === nextParams.latitude &&
      this.lastEnvironmentalVariablesParams.longitude === nextParams.longitude
    );
  }

  /** Public wrapper: true when cached env-var params match the given params */
  hasMatchingEnvironmentalVariablesParams(params: {
    macroRegionOrigin: string;
    stationId?: number;
    latitude?: number;
    longitude?: number;
  }): boolean {
    return (
      this.environmentalVariables.length > 0 && this.hasMatchingParams(params)
    );
  }

  async getEnvironmentalVariables(params: {
    macroRegionOrigin: string;
    stationId?: number;
    latitude?: number;
    longitude?: number;
    forceReload?: boolean;
  }) {
    if (this.isLoadingEnvironmentalVariables) {
      return;
    }

    const nextParams = {
      macroRegionOrigin: params.macroRegionOrigin,
      stationId: params.stationId,
      latitude: params.latitude,
      longitude: params.longitude,
    };

    const canUseCachedData =
      !params.forceReload &&
      this.environmentalVariables.length > 0 &&
      this.hasMatchingParams(nextParams);

    if (canUseCachedData) {
      return;
    }

    const { insightAlertService } = getEnv<RootEnv>();
    this.lastEnvironmentalVariablesParams = nextParams;
    try {
      this.isLoadingEnvironmentalVariables = true;
      this.hasErrorLoadingEnvironmentalVariables = false;
      this.setAlertDetailState(StoreState.PEDDING);
      const variables: IEnvironmentalVariable[] =
        await insightAlertService.getEnvironmentalVariables(params);

      this.setEnvironmentalVariables(variables);

      this.setAlertDetailState(StoreState.DONE);
    } catch (error) {
      this.hasErrorLoadingEnvironmentalVariables = true;
      this.setAlertDetailState(StoreState.ERROR);
      this.setMessageError(
        i18n.t("oceanWeatherIntelligence.insightAlert.errorGetAlertsData"),
      );
    } finally {
      this.isLoadingEnvironmentalVariables = false;
    }
  }

  async retryLoadEnvironmentalVariables() {
    if (!this.lastEnvironmentalVariablesParams) return;
    await this.getEnvironmentalVariables({
      ...this.lastEnvironmentalVariablesParams,
      forceReload: true,
    });
  }

  clearFilters() {
    this.setIsFilterApplied(false);
    this.setAlertTypeDetailSelected(null);
    this.setOperationRuleDetailSelected(0);
    this.setOperationRuleLocationDetailSelected(0);

    this.getOperationRuleAlertForTableAPI({});
  }
}
